import { useState } from "react";
import { ModalShell } from "./ModalShell";
import { ModalValHint } from "./ModalValHint";
import { useGatedAction } from "./useGatedAction";
import { Autocomplete } from "../ui/Autocomplete";
import { DateField } from "../ui/DateField";
import { US_CITIES } from "../../lib/usCities";
import type { CreateCustomEventInput, EventDistance } from "../../lib/eventsApi";
import "../Planner/planner.css";
import "./modals.css";

/**
 * Add Event modal (journey §3-4, fixes B5). Name + date + location + a
 * distance-category chip row. Built on the shared `ModalShell`; saving goes
 * through the caller's `onSave`, which hits the v3 events API (`POST
 * /api/events`) via `useEvents`.
 *
 * Location uses the generic `<Autocomplete>` primitive over `US_CITIES`
 * (replacing the prototype's bespoke `CityAutocomplete` — docs/v4/03 §C2).
 */

const DISTANCES: { id: EventDistance; label: string }[] = [
  { id: "5k", label: "5K" },
  { id: "10k", label: "10K" },
  { id: "half", label: "Half marathon" },
  { id: "marathon", label: "Marathon" },
  { id: "ultra", label: "Ultra" },
  { id: "triathlon", label: "Triathlon" },
  { id: "hyrox", label: "Hyrox" },
  { id: "other", label: "Other" }
];

function distanceFromCatalogPrefill(
  initialDistance: EventDistance | null | undefined
): EventDistance {
  if (initialDistance === undefined) return "marathon";
  if (initialDistance === null) return "other";
  return initialDistance;
}

export function AddEventModal({
  onClose,
  onSave,
  initialName = "",
  /** `YYYY-MM-DD` (UTC) — from catalog `eventDate` prefix or planner entry. */
  initialEventDate = "",
  /** “City, ST” seed for optional location autocomplete. */
  initialLocation = "",
  initialDistance,
  /** When true, no distance chip is preselected — the user must pick one. */
  initialDistanceUnset = false,
  /** Inclusive earliest selectable date (`YYYY-MM-DD`). Onboarding passes today
   *  so a race being trained for can't be dated in the past. */
  minDate
}: {
  onClose: () => void;
  /** Persist via `POST /api/events` using the custom-event body shape only. */
  onSave: (input: CreateCustomEventInput) => Promise<void>;
  initialName?: string;
  initialEventDate?: string;
  initialLocation?: string;
  initialDistance?: EventDistance | null;
  initialDistanceUnset?: boolean;
  minDate?: string;
}) {
  const [name, setName] = useState(initialName);
  const [date, setDate] = useState(initialEventDate);
  const [location, setLocation] = useState(initialLocation);
  const [distance, setDistance] = useState<EventDistance | null>(() =>
    initialDistanceUnset ? null : distanceFromCatalogPrefill(initialDistance)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dateBeforeMin = !!minDate && date.length > 0 && date < minDate;
  const canSave =
    name.trim().length > 0 && date.length > 0 && distance !== null && !dateBeforeMin && !saving;

  const handleSave = async () => {
    if (!canSave || distance === null) return;
    setSaving(true);
    setError(null);
    // The location field carries "City, ST" — split it for the API.
    const trimmed = location.trim();
    const [city, region] = trimmed.split(",").map((s) => s.trim());
    const input: CreateCustomEventInput = {
      name: name.trim(),
      eventDate: date,
      distance,
      ...(city ? { locationCity: city } : {}),
      ...(region ? { locationRegion: region } : {})
    };
    try {
      await onSave(input);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't add that event.");
    } finally {
      setSaving(false);
    }
  };

  const saveGate = useGatedAction({
    canProceed: canSave,
    busy: saving,
    validate: () => {
      if (!name.trim()) return "Enter an event name.";
      if (!date) return "Pick a date for this event.";
      if (dateBeforeMin) return "Pick an upcoming date — the event can't be in the past.";
      if (distance === null) return "Pick a distance to continue.";
      return null;
    },
    onProceed: () => void handleSave(),
    autoClearWhen: canSave
  });

  return (
    <ModalShell
      eyebrow="ADD EVENT"
      title="Add a custom event"
      onClose={onClose}
      width={560}
      footer={
        <>
          <span />
          <div className="fgm-footer-actions">
            <button type="button" className="fg-btn fg-btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className={`fg-btn fg-btn--primary${saveGate.gateBtnClass}`}
              aria-disabled={saveGate.ariaDisabled}
              onClick={saveGate.handleClick}>
              {saving ? "Adding…" : "Add event"}
            </button>
          </div>
        </>
      }>
      <div className="fgp-modalshell__pad fgp-form__body">
        {error && (
          <div role="alert" className="fgm-ae-error">
            {error}
          </div>
        )}
        <ModalValHint message={saveGate.nudge} />

        <Field label="Event name">
          <input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              saveGate.clearNudge();
            }}
            className="fg-input"
            autoFocus
            aria-required="true"
            placeholder="e.g. My local 10K"
          />
        </Field>

        <div className="fgp-wizard-grid-2">
          <Field label="Date">
            <DateField
              value={date}
              onChange={(next) => {
                setDate(next);
                saveGate.clearNudge();
              }}
              min={minDate}
              showLongFormat={false}
              aria-label="Event date"
            />
          </Field>
          <Field label="Location" hint="Optional">
            <Autocomplete<string>
              value={location}
              onChange={setLocation}
              getOptions={(q) => {
                const query = q.trim().toLowerCase();
                if (!query) return [];
                return US_CITIES.filter((c) => c.toLowerCase().includes(query)).slice(0, 8);
              }}
              optionKey={(c) => c}
              renderOption={(c) => c}
              onPick={(c) => setLocation(c)}
              placeholder="e.g. Boston, MA"
              ariaLabel="Event location"
            />
          </Field>
        </div>

        <Field label="Distance / format" labelId="fgm-ae-distance-label">
          <div
            className="fgm-ae-distances"
            role="radiogroup"
            aria-labelledby="fgm-ae-distance-label">
            {DISTANCES.map((d) => {
              const on = distance === d.id;
              return (
                <button
                  key={d.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setDistance(d.id)}
                  className={`fg-chip${on ? " is-on" : ""}`}>
                  {d.label}
                </button>
              );
            })}
          </div>
        </Field>
      </div>
    </ModalShell>
  );
}

function Field({
  label,
  hint,
  hintBelow = false,
  labelId,
  children
}: {
  label: string;
  hint?: string;
  /** When true, helper text renders under the control (default keeps it beside the label). */
  hintBelow?: boolean;
  /** Optional id on the label text so a grouped control can `aria-labelledby` it. */
  labelId?: string;
  children: React.ReactNode;
}) {
  const hintEl = hint ? <span className="fgp-form__hint">{hint}</span> : null;

  return (
    <label className="fgp-col-gap8">
      {hintBelow ? (
        <span id={labelId} className="fgp-form__label-text">{label}</span>
      ) : (
        <div className="fgp-row-baseline-gap10-wrap">
          <span id={labelId} className="fgp-form__label-text">{label}</span>
          {hintEl}
        </div>
      )}
      {children}
      {hintBelow ? hintEl : null}
    </label>
  );
}
